import React, { useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, CopyIcon, CheckIcon, XIcon, PlayIcon, HistoryIcon, GitBranchIcon, SaveIcon, EyeIcon, CodeIcon, RefreshCwIcon, TagIcon, ToggleLeftIcon, ToggleRightIcon, DollarSignIcon, LinkIcon, LayersIcon, AlertTriangleIcon, CheckCircleIcon } from 'lucide-react';
import { StatusBadge } from '../StatusBadge';
import { RuleSetEditor } from './rules/RuleSetEditor';
import { TestRunner } from './rules/TestRunner';
import { OrgUnitSelector } from '../settings/orgunit/OrgUnitSelector';
import type { OrgUnit } from './sharedFinancialData';
type RuleSet = {
  id: string;
  name: string;
  description: string;
  module: string;
  triggerEvent: string;
  status: 'Active' | 'Draft' | 'Inactive';
  scope: 'Global' | 'OrgUnit';
  orgUnitId?: string;
  orgUnitName?: string;
  orgUnitPath?: string;
  lastModified: string;
  version: string;
  ruleType?: string;
  documentTypes?: string[];
  execution?: string;
  parameters?: any[];
  conditions: any[];
  actions: any[];
  linkedRules?: any[];
  jsonOutput?: string;
};
export function RulesManager({
  locale
}) {
  const [activeView, setActiveView] = useState('overview');
  const [selectedRuleSet, setSelectedRuleSet] = useState<RuleSet | null>(null);
  const [showTestRunner, setShowTestRunner] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Sample data for rule sets
  const [ruleSets, setRuleSets] = useState<RuleSet[]>([{
    id: 'rule-001',
    name: 'Invoice Approval Workflow',
    description: 'Determines the approval path for invoices based on amount, vendor, and GL code',
    module: 'AP',
    triggerEvent: 'Invoice Submitted',
    status: 'Active',
    scope: 'Global',
    lastModified: '2023-10-15T14:30:00Z',
    version: '1.2',
    conditions: [{
      id: 'cond-001',
      type: 'group',
      operator: 'AND',
      children: [{
        id: 'cond-002',
        type: 'condition',
        field: 'Value',
        operator: 'greaterThan',
        value: '10000'
      }, {
        id: 'cond-003',
        type: 'condition',
        field: 'GL Code',
        operator: 'equals',
        value: '5000.5200'
      }]
    }],
    actions: [{
      id: 'action-001',
      type: 'route',
      target: 'Finance Manager',
      targetType: 'role'
    }, {
      id: 'action-002',
      type: 'notify',
      target: 'john.smith@example.com',
      targetType: 'email',
      template: 'High Value Invoice'
    }]
  }, {
    id: 'rule-002',
    name: 'PO Auto-Approval',
    description: 'Automatically approves purchase orders under certain conditions',
    module: 'Procurement',
    triggerEvent: 'PO Created',
    status: 'Draft',
    scope: 'Global',
    lastModified: '2023-10-10T09:15:00Z',
    version: '0.5',
    conditions: [{
      id: 'cond-004',
      type: 'group',
      operator: 'OR',
      children: [{
        id: 'cond-005',
        type: 'condition',
        field: 'Value',
        operator: 'lessThan',
        value: '1000'
      }, {
        id: 'cond-006',
        type: 'condition',
        field: 'Vendor',
        operator: 'in',
        value: ['vendor-001', 'vendor-002']
      }]
    }],
    actions: [{
      id: 'action-003',
      type: 'auto-approve'
    }]
  }, {
    id: 'rule-003',
    name: 'Singapore High-Value Approval',
    description: 'Special approval workflow for high-value transactions in Singapore office',
    module: 'AP',
    triggerEvent: 'Invoice Submitted',
    status: 'Active',
    scope: 'OrgUnit',
    orgUnitId: 'ou-apac-sg',
    orgUnitName: 'Singapore Office',
    orgUnitPath: 'Global Technologies Inc. > APAC Region > Singapore Office',
    lastModified: '2023-11-01T10:15:00Z',
    version: '1.0',
    conditions: [{
      id: 'cond-sg-001',
      type: 'condition',
      field: 'Value',
      operator: 'greaterThan',
      value: '50000'
    }],
    actions: [{
      id: 'action-sg-001',
      type: 'route',
      target: 'Regional Director',
      targetType: 'role'
    }, {
      id: 'action-sg-002',
      type: 'notify',
      target: 'compliance@sg.example.com',
      targetType: 'email'
    }]
  }, {
    id: 'rule-004',
    name: 'Mexico Vendor Compliance',
    description: 'Compliance checks for vendors in Mexico operations',
    module: 'Vendor',
    triggerEvent: 'Vendor Created',
    status: 'Active',
    scope: 'OrgUnit',
    orgUnitId: 'ou-latam-mx',
    orgUnitName: 'Mexico Office',
    orgUnitPath: 'Global Technologies Inc. > LATAM Region > Mexico Office',
    lastModified: '2023-10-28T16:20:00Z',
    version: '1.1',
    conditions: [{
      id: 'cond-mx-001',
      type: 'condition',
      field: 'Country',
      operator: 'equals',
      value: 'MX'
    }],
    actions: [{
      id: 'action-mx-001',
      type: 'trigger-workflow',
      workflowId: 'mx-compliance'
    }, {
      id: 'action-mx-002',
      type: 'notify',
      target: 'compliance-mx',
      targetType: 'group'
    }]
  }]);
  // Sample data for available modules and triggers
  const modules = [{
    id: 'AP',
    name: 'Accounts Payable'
  }, {
    id: 'Procurement',
    name: 'Procurement'
  }, {
    id: 'Vendor',
    name: 'Vendor Management'
  }, {
    id: 'GL',
    name: 'General Ledger'
  }, {
    id: 'Expense',
    name: 'Expense Management'
  }, {
    id: 'Financial',
    name: 'Financial Management'
  }];
  const triggerEvents = {
    AP: [{
      id: 'Invoice Submitted',
      name: 'Invoice Submitted'
    }, {
      id: 'Invoice Approved',
      name: 'Invoice Approved'
    }, {
      id: 'Payment Initiated',
      name: 'Payment Initiated'
    }],
    Procurement: [{
      id: 'PO Created',
      name: 'PO Created'
    }, {
      id: 'PO Approved',
      name: 'PO Approved'
    }, {
      id: 'Requisition Submitted',
      name: 'Requisition Submitted'
    }],
    Vendor: [{
      id: 'Vendor Created',
      name: 'Vendor Created'
    }, {
      id: 'Vendor Updated',
      name: 'Vendor Updated'
    }, {
      id: 'Vendor Approved',
      name: 'Vendor Approved'
    }],
    GL: [{
      id: 'Journal Entry Created',
      name: 'Journal Entry Created'
    }, {
      id: 'Period Closing',
      name: 'Period Closing'
    }],
    Expense: [{
      id: 'Expense Report Submitted',
      name: 'Expense Report Submitted'
    }, {
      id: 'Expense Approved',
      name: 'Expense Approved'
    }],
    Financial: [{
      id: 'Document Submitted',
      name: 'Document Submitted'
    }, {
      id: 'Document Approved',
      name: 'Document Approved'
    }, {
      id: 'Document Rejected',
      name: 'Document Rejected'
    }]
  };
  // Sample data for reusable conditions
  const [reusableConditions, setReusableConditions] = useState([{
    id: 'template-001',
    name: 'High Value Transaction',
    description: 'Identifies transactions above $10,000',
    condition: {
      id: 'cond-temp-001',
      type: 'condition',
      field: 'Value',
      operator: 'greaterThan',
      value: '10000'
    }
  }, {
    id: 'template-002',
    name: 'High Risk Country',
    description: 'Identifies transactions from high risk countries',
    condition: {
      id: 'cond-temp-002',
      type: 'condition',
      field: 'Country',
      operator: 'in',
      value: ['RU', 'CN', 'IR', 'KP', 'SY']
    }
  }]);
  // Handler for creating a new rule set
  const handleCreateRuleSet = () => {
    const newRuleSet: RuleSet = {
      id: `rule-${Date.now()}`,
      name: 'New Rule Set',
      description: '',
      module: '',
      triggerEvent: '',
      status: 'Draft',
      scope: 'Global',
      lastModified: new Date().toISOString(),
      version: '0.1',
      conditions: [],
      actions: []
    };
    setSelectedRuleSet(newRuleSet);
    setActiveView('edit');
  };
  // Handler for editing a rule set
  const handleEditRuleSet = (ruleSet: RuleSet) => {
    setSelectedRuleSet(ruleSet);
    setActiveView('edit');
  };
  // Handler for duplicating a rule set
  const handleDuplicateRuleSet = (ruleSet: RuleSet) => {
    const duplicatedRuleSet: RuleSet = {
      ...ruleSet,
      id: `rule-${Date.now()}`,
      name: `Copy of ${ruleSet.name}`,
      status: 'Draft',
      lastModified: new Date().toISOString(),
      version: '0.1'
    };
    setRuleSets([...ruleSets, duplicatedRuleSet]);
  };
  // Handler for activating/deactivating a rule set
  const handleToggleRuleSetStatus = (ruleSetId: string) => {
    setRuleSets(ruleSets.map(rs => rs.id === ruleSetId ? {
      ...rs,
      status: rs.status === 'Active' ? 'Inactive' : rs.status === 'Inactive' ? 'Active' : rs.status,
      lastModified: new Date().toISOString()
    } : rs));
  };
  // Handler for deleting a rule set
  const handleDeleteRuleSet = (ruleSetId: string) => {
    setDeletingId(ruleSetId);
    setShowDeleteConfirm(true);
  };
  const confirmDelete = () => {
    if (deletingId) {
      setRuleSets(ruleSets.filter(rs => rs.id !== deletingId));
      setShowDeleteConfirm(false);
      setDeletingId(null);
    }
  };
  // Handler for testing a rule set
  const handleTestRuleSet = (ruleSet: RuleSet) => {
    setSelectedRuleSet(ruleSet);
    setShowTestRunner(true);
    setActiveView('edit');
  };
  // Handler for saving a rule set
  const handleSaveRuleSet = (updatedRuleSet: RuleSet) => {
    if (updatedRuleSet.id && ruleSets.find(rs => rs.id === updatedRuleSet.id)) {
      // Update existing rule set
      setRuleSets(ruleSets.map(rs => rs.id === updatedRuleSet.id ? {
        ...updatedRuleSet,
        lastModified: new Date().toISOString()
      } : rs));
    } else {
      // Create new rule set
      const newRuleSet: RuleSet = {
        ...updatedRuleSet,
        id: updatedRuleSet.id || `rule-${Date.now()}`,
        lastModified: new Date().toISOString()
      };
      setRuleSets([...ruleSets, newRuleSet]);
    }
    setActiveView('overview');
    setSelectedRuleSet(null);
  };
  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Get rule type icon
  const getRuleTypeIcon = (ruleSet: RuleSet) => {
    if (ruleSet.ruleType === 'Financial Delegation') {
      return <DollarSignIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-11855" />;
    } else if (ruleSet.module === 'Vendor') {
      return <TagIcon className="h-4 w-4 text-orange-500 mr-2" data-id="element-11856" />;
    } else {
      return <GitBranchIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-11857" />;
    }
  };
  const globalRuleSets = ruleSets.filter(rs => rs.scope === 'Global');
  const orgUnitRuleSets = ruleSets.filter(rs => rs.scope === 'OrgUnit');
  // Render the Rule Sets overview
  const renderRuleSetsOverview = () => {
    return <div className="space-y-6" data-id="element-11858">
        {/* Global Rule Sets */}
        <div className="bg-white shadow rounded-lg" data-id="element-11859">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-11860">
            <div className="flex justify-between items-center" data-id="element-11861">
              <div data-id="element-11862">
                <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11863">
                  Global Rule Sets
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-11864">
                  Global rules available to all organisation units
                </p>
              </div>
              <button onClick={handleCreateRuleSet} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11865">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-11866" />
                Create Rule Set
              </button>
            </div>
          </div>

          {globalRuleSets.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-11867">
              <p className="text-sm text-gray-500" data-id="element-11868">
                No global rule sets configured.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-11869">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-11870">
                <thead className="bg-gray-50" data-id="element-11871">
                  <tr data-id="element-11872">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11873">
                      Rule Set
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11874">
                      Module / Trigger
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11875">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11876">
                      Version
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11877">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-11878">
                  {globalRuleSets.map(ruleSet => <tr key={ruleSet.id} data-id="element-11879">
                      <td className="px-6 py-4" data-id="element-11880">
                        <div className="flex items-center" data-id="element-11881">
                          {getRuleTypeIcon(ruleSet)}
                          <div data-id="element-11882">
                            <div className="text-sm font-medium text-gray-900" data-id="element-11883">
                              {ruleSet.name}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-11884">
                              {ruleSet.description}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-11885">
                        <div className="text-sm text-gray-900" data-id="element-11886">
                          {ruleSet.module}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-11887">
                          {ruleSet.triggerEvent}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-11888">
                        {ruleSet.status === 'Active' && <StatusBadge status="success" text="Active" data-id="element-11889" />}
                        {ruleSet.status === 'Draft' && <StatusBadge status="info" text="Draft" data-id="element-11890" />}
                        {ruleSet.status === 'Inactive' && <StatusBadge status="error" text="Inactive" data-id="element-11891" />}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-11892">
                        <div className="text-sm text-gray-900" data-id="element-11893">
                          v{ruleSet.version}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-11894">
                        <div className="flex justify-end space-x-2" data-id="element-11895">
                          <button onClick={() => handleEditRuleSet(ruleSet)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-11896">
                            <EditIcon className="h-4 w-4" data-id="element-11897" />
                          </button>
                          <button onClick={() => handleTestRuleSet(ruleSet)} className="text-blue-600 hover:text-blue-900" title="Test" data-id="element-11898">
                            <PlayIcon className="h-4 w-4" data-id="element-11899" />
                          </button>
                          <button onClick={() => handleDuplicateRuleSet(ruleSet)} className="text-green-600 hover:text-green-900" title="Duplicate" data-id="element-11900">
                            <CopyIcon className="h-4 w-4" data-id="element-11901" />
                          </button>
                          <button onClick={() => handleDeleteRuleSet(ruleSet.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-11902">
                            <TrashIcon className="h-4 w-4" data-id="element-11903" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>

        {/* OrgUnit Rule Sets */}
        <div className="bg-white shadow rounded-lg" data-id="element-11904">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-11905">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11906">
              Organisation Unit Rule Sets
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-11907">
              Rules specific to individual organisation units
            </p>
          </div>

          {orgUnitRuleSets.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-11908">
              <p className="text-sm text-gray-500" data-id="element-11909">
                No organisation unit rule sets configured.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-11910">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-11911">
                <thead className="bg-gray-50" data-id="element-11912">
                  <tr data-id="element-11913">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11914">
                      Organisation Unit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11915">
                      Rule Set
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11916">
                      Module / Trigger
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11917">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11918">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-11919">
                  {orgUnitRuleSets.map(ruleSet => <tr key={ruleSet.id} data-id="element-11920">
                      <td className="px-6 py-4" data-id="element-11921">
                        <div className="text-sm font-medium text-gray-900" data-id="element-11922">
                          {ruleSet.orgUnitName}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-11923">
                          {ruleSet.orgUnitPath}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-11924">
                        <div className="flex items-center" data-id="element-11925">
                          {getRuleTypeIcon(ruleSet)}
                          <div data-id="element-11926">
                            <div className="text-sm font-medium text-gray-900" data-id="element-11927">
                              {ruleSet.name}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-11928">
                              {ruleSet.description}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-11929">
                        <div className="text-sm text-gray-900" data-id="element-11930">
                          {ruleSet.module}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-11931">
                          {ruleSet.triggerEvent}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-11932">
                        {ruleSet.status === 'Active' && <StatusBadge status="success" text="Active" data-id="element-11933" />}
                        {ruleSet.status === 'Draft' && <StatusBadge status="info" text="Draft" data-id="element-11934" />}
                        {ruleSet.status === 'Inactive' && <StatusBadge status="error" text="Inactive" data-id="element-11935" />}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-11936">
                        <div className="flex justify-end space-x-2" data-id="element-11937">
                          <button onClick={() => handleEditRuleSet(ruleSet)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-11938">
                            <EditIcon className="h-4 w-4" data-id="element-11939" />
                          </button>
                          <button onClick={() => handleTestRuleSet(ruleSet)} className="text-blue-600 hover:text-blue-900" title="Test" data-id="element-11940">
                            <PlayIcon className="h-4 w-4" data-id="element-11941" />
                          </button>
                          <button onClick={() => handleDuplicateRuleSet(ruleSet)} className="text-green-600 hover:text-green-900" title="Duplicate" data-id="element-11942">
                            <CopyIcon className="h-4 w-4" data-id="element-11943" />
                          </button>
                          <button onClick={() => handleDeleteRuleSet(ruleSet.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-11944">
                            <TrashIcon className="h-4 w-4" data-id="element-11945" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </div>;
  };
  return <div className="space-y-6" data-id="element-11946">
      <div className="mb-8" data-id="element-11947">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-11948">Rules</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-11949">
          Define conditional logic for business process automation at global and
          organisation unit levels
        </p>
      </div>

      {/* Main Content */}
      {activeView === 'overview' ? renderRuleSetsOverview() : <div className="space-y-4" data-id="element-11950">
          <div className="flex justify-between items-center mb-4" data-id="element-11951">
            <button onClick={() => {
          setActiveView('overview');
          setSelectedRuleSet(null);
          setShowTestRunner(false);
        }} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-11952">
              ← Back to Rule Sets
            </button>
            <div className="flex space-x-2" data-id="element-11953">
              {!showTestRunner && <button onClick={() => setShowTestRunner(true)} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-11954">
                  <PlayIcon className="h-4 w-4 mr-2" data-id="element-11955" />
                  Test Rule
                </button>}
              {showTestRunner && <button onClick={() => setShowTestRunner(false)} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-11956">
                  <EditIcon className="h-4 w-4 mr-2" data-id="element-11957" />
                  Edit Rule
                </button>}
            </div>
          </div>
          <div className={`grid grid-cols-1 ${showTestRunner ? 'lg:grid-cols-2' : ''} gap-6`} data-id="element-11958">
            {!showTestRunner && <div className="col-span-1" data-id="element-11959">
                <RuleSetEditor ruleSet={selectedRuleSet} modules={modules} triggerEvents={triggerEvents} reusableConditions={reusableConditions} onSave={handleSaveRuleSet} locale={locale} data-id="element-11960" />
              </div>}
            {showTestRunner && <>
                <div className="col-span-1" data-id="element-11961">
                  <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-11962">
                    <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-11963">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11964">
                        Rule Set Details
                      </h3>
                    </div>
                    <div className="px-4 py-5 sm:p-6" data-id="element-11965">
                      <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2" data-id="element-11966">
                        <div className="sm:col-span-1" data-id="element-11967">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-11968">
                            Name
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-11969">
                            {selectedRuleSet?.name}
                          </dd>
                        </div>
                        <div className="sm:col-span-1" data-id="element-11970">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-11971">
                            Version
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-11972">
                            v{selectedRuleSet?.version}
                          </dd>
                        </div>
                        <div className="sm:col-span-1" data-id="element-11973">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-11974">
                            Module
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-11975">
                            {selectedRuleSet?.module}
                          </dd>
                        </div>
                        <div className="sm:col-span-1" data-id="element-11976">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-11977">
                            Trigger
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-11978">
                            {selectedRuleSet?.triggerEvent}
                          </dd>
                        </div>
                        <div className="sm:col-span-2" data-id="element-11979">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-11980">
                            Description
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-11981">
                            {selectedRuleSet?.description}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </div>
                </div>
                <div className="col-span-1" data-id="element-11982">
                  <TestRunner ruleSet={selectedRuleSet} locale={locale} data-id="element-11983" />
                </div>
              </>}
          </div>
        </div>}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-11984">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-11985">
            <div className="fixed inset-0 transition-opacity" data-id="element-11986">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-11987"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" data-id="element-11988">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-11989">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-11990">
                <div className="sm:flex sm:items-start" data-id="element-11991">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-11992">
                    <AlertTriangleIcon className="h-6 w-6 text-red-600" data-id="element-11993" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-11994">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11995">
                      Delete Rule Set
                    </h3>
                    <div className="mt-2" data-id="element-11996">
                      <p className="text-sm text-gray-500" data-id="element-11997">
                        Are you sure you want to delete this rule set? This
                        action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-11998">
                <button type="button" onClick={confirmDelete} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-11999">
                  Delete
                </button>
                <button type="button" onClick={() => setShowDeleteConfirm(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-12000">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}